import { useState, useCallback, useMemo } from "react";

import { usePulseApiToken } from "@/features/auth/hooks/useApiToken";
import { toast } from "sonner";
import { useAuthStore } from "@/features/auth/store/useAuthStore";
import { copyToClipboard } from "@/lib/clipboard";
import TwoColumnLayout from "@/components/Layout/TwoColumnLayout";
import { MONITORING_TYPES } from "@/constants/monitoring";
import { Button } from "@/components/ui/button";
import { useNavigate } from "@tanstack/react-router";
import PulseList from "../components/Pulse/PulseList";
import { useTokenManagement } from "@/hooks/useTokenManagement";
import { useGetPulsesWithPostData } from "../api/pulse.queries";

export default function PulseContainer() {
  const [showResults, setShowResults] = useState(false);
  const [, setIsLaunching] = useState(false);
  const [hasLaunched, setHasLaunched] = useState(true);

  const { apiToken, isPending: isApiTokenLoading } = usePulseApiToken();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { data, isLoading, error, pulsesData } = useGetPulsesWithPostData();

  // Combined loading state: either waiting for token OR waiting for pulse data
  const isActuallyLoading = isApiTokenLoading || (isLoading && !!apiToken);

  // Token management with dropdown functionality
  const {
    selectedToken,
    effectiveToken,
    handleTokenSelect,
    availableTokens,
    tokensLoading,
  } = useTokenManagement({
    apiToken,
    isApiTokenLoading,
    user,
    showTokenDropdown: true,
    additionalValidation: () => true,
    monitoringType: "pulse",
  });
  const handleCopyCode = useCallback(() => {
    copyToClipboard("{}");
    toast.success("Copied to clipboard");
  }, []);

  const handleLaunch = useCallback(async () => {
    if (!showResults) {
      setIsLaunching(true);
      setTimeout(() => {
        setShowResults(true);
        setHasLaunched(true);
        setIsLaunching(false);
      }, 300);
    }
  }, [showResults]);

  const handlePanelResize = useCallback((sizes: number[]) => {
    const rightPanelSize = sizes[1];
    if (rightPanelSize < 5) {
      setShowResults(false);
    }
  }, []);

  const apiEndpoint = "";

  const handleExportJSON = useCallback(() => {
    toast.info("JSON export coming soon");
  }, []);

  const handleExportCSV = useCallback(() => {
    toast.info("CSV export coming soon");
  }, []);

  const content = useMemo(
    () => (
      <div className="flex h-full min-h-0 flex-col overflow-x-hidden">
        <div
          className="scrollbar-thin flex-1 overflow-x-hidden overflow-y-auto"
          style={{
            transform: "translateZ(0)",
            scrollBehavior: "auto",
            overscrollBehavior: "contain",
          }}
        >
          <PulseList data={data} isLoading={isActuallyLoading} error={error} />
        </div>
      </div>
    ),
    [data, isActuallyLoading, error],
  );

  const bottomContent = useMemo(
    () => (
      <div className="bg-brand-50 flex h-[72px] w-full flex-row items-end justify-start gap-3 py-3">
        <Button
          variant="default"
          icon
          className="flex h-[48px] w-[240px] gap-2 hover:scale-none"
          onClick={() =>
            navigate({ to: "/pulse/create", search: { mode: "create" } })
          }
        >
          <i className="ri-add-line text-[16px] font-light"></i> Create new
          pulse
        </Button>
      </div>
    ),
    [navigate],
  );

  return (
    <TwoColumnLayout
      data={pulsesData}
      isLoading={false}
      error={null}
      responseJSON={JSON.stringify(pulsesData, null, 2)}
      apiEndpoint={apiEndpoint}
      apiToken={effectiveToken}
      isApiTokenLoading={isApiTokenLoading}
      resultsTitle="Results"
      showResults={showResults}
      onShowResults={setShowResults}
      onLaunch={handleLaunch}
      onCopyCode={handleCopyCode}
      handleExportCSV={handleExportCSV}
      handleExportJSON={handleExportJSON}
      floatingButtonCondition={hasLaunched}
      leftPanelMinSize={50}
      rightPanelMinSize={30}
      onPanelResize={handlePanelResize}
      bottomContent={bottomContent}
      showTokenDropdown={true}
      onTokenSelect={handleTokenSelect}
      monitoringType={MONITORING_TYPES.PULSE}
      selectedToken={selectedToken}
      availableTokens={availableTokens}
      tokensLoading={tokensLoading}
    >
      {content}
    </TwoColumnLayout>
  );
}
